/**
 * Tool that abstracts the details of handling different types of resource sources. Handles Cesium Ion and plain URL
 * sources.
 *
 * This includes functionality for loading resources relative to an owner, as well as creating new 'ResourceSource'
 * instances that inherit authentication state from their parent.
 */
export class ResourceSource {
    /**
     * Private constructor, forces users to use one of the static builder functions
     * @param source
     * @private
     */
    constructor(source) {
        this._source = source;
    }
    /**
     * Constructs a 'ResourceSource' from the provided URL. Has no associated authentication information, the target URL
     * must be publicly accessible.
     * @param url The URL
     */
    static fromDirectEndpoint(url, absolute) {
        return new ResourceSource({
            type: "direct",
            url,
            absolute: absolute !== null && absolute !== void 0 ? absolute : false,
        });
    }
    /**
     * Constructs a 'ResourceSource' from the provided Cesium Ion endpoint. The included authorization token and
     * authorization are wrapped and exposed via the created instance.
     *
     * This will also assert that 'url' and 'type' exist on the endpoint.
     *
     * @param endpoint The endpoint
     */
    static fromCesiumEndpoint(endpoint) {
        // Check that these fields are present as we require them to exist (but may be missing as fetched from
        // the cesium API)
        if (endpoint.url === undefined)
            throw new Error("'TilesetSource' missing 'url' attribute");
        if (endpoint.type === undefined)
            throw new Error("'TilesetSource' missing 'type' attribute");
        return new ResourceSource({
            type: "cesium",
            endpoint: {
                url: endpoint.url,
                type: endpoint.type,
                accessToken: endpoint.accessToken,
                attributions: endpoint.attributions,
            },
        });
    }
    /**
     * Returns an absolute URL to a resource relative to this resource based on the provided path.
     *
     * Very important for formats which refer to other files with relative paths inside the file.
     *
     * @param path Relative or absolute path
     */
    adjacentResource(path) {
        // If the source paths are absolute, then we can just return the new path
        // Otherwise, we must make it relative to the source URL
        if (this._source.type === "direct" && this._source.absolute) {
            const url = new URL(this.url);
            const pathSplit = path.split("?");
            url.pathname = pathSplit[0];
            const pathParams = new URLSearchParams(pathSplit[1]);
            for (const [key, value] of pathParams) {
                url.searchParams.append(key, value);
            }
            return url.toString();
        }
        else {
            const split = this.url.split("?");
            if (split.length === 1) {
                return `${split[0]}/../${path}`;
            }
            else {
                return `${split[0]}/../${path}?${split[1]}`;
            }
        }
    }
    /**
     * The URL of the resource being wrapped.
     */
    get url() {
        switch (this._source.type) {
            case "direct":
                return this._source.url;
            case "cesium":
                return this._source.endpoint.url;
        }
    }
    /**
     * The access token string for this resource, if present.
     */
    get accessToken() {
        switch (this._source.type) {
            case "direct":
                return undefined;
            case "cesium":
                return this._source.endpoint.accessToken;
        }
    }
    /**
     * Returns a 'bearer token' header if the wrapped resource uses one.
     */
    get bearerToken() {
        const token = this.accessToken;
        if (token !== undefined) {
            return {
                Authorization: `Bearer ${token}`,
            };
        }
        else {
            return undefined;
        }
    }
    /**
     * Returns the array of 'Attribution' infos associated with this resource
     */
    get attributions() {
        var _a;
        switch (this._source.type) {
            case "direct":
                return [];
            case "cesium":
                return (_a = this._source.endpoint.attributions) !== null && _a !== void 0 ? _a : [];
        }
    }
    /**
     * Constructs a new 'ResourceSource' with the given URL that inherits the remaining source state from 'this'.
     *
     * For example, for a Cesium Ion source, this function will produce a new 'ResourceSource' that will point to 'url'
     * but will use the same authorization token and attribution information.
     *
     * This is important for resources with auth tokens and multiple files to pull (like 3D Tiles). Without this tool
     * we would have to track the auth token manually. 'ResourceSource' encapsulates all the logic for particular
     * sources into a single uniform interface.
     *
     * @param url Absolute URL that will inherit from 'this'
     */
    inherit(url) {
        switch (this._source.type) {
            case "direct": {
                return new ResourceSource({
                    type: "direct",
                    url,
                    absolute: this._source.absolute,
                });
            }
            case "cesium": {
                return new ResourceSource({
                    type: "cesium",
                    endpoint: {
                        url,
                        type: this._source.endpoint.type,
                        accessToken: this._source.endpoint.accessToken,
                        attributions: this._source.endpoint.attributions,
                    },
                });
            }
        }
    }
}
